import type { CollisionBrush, CollisionRule, CollisionRules, TerrainDefinition } from "daedalus-core";

/**
 * OdysseyTales' collision byte (Playbook 5.8, ADR-028): bit flags the server's movement reads from the
 * chunk collision grid, and the rules that derive them from the terrains a world uses. Rules match by wang
 * set name because the packs name walls, holes and water consistently while their ids carry pack prefixes.
 */

/**
 * The cell cannot be entered.
 */
export const SOLID = 1;

/**
 * The cell is water.
 */
export const WATER = 2;

/**
 * The cell is a hole.
 */
export const HOLE = 4;

/**
 * Paint that opens a cell (ADR-017's and ADR-028's amendments of 2026-10-04): where a collision layer holds
 * it, the rules over the terrain write nothing, so a river under a bridge can be crossed, while a prop, a
 * fence or `SOLID` paint on the cell still blocks. It is the rules' open mask and lives in the map document
 * only: the compile strips it, so no compiled grid, and so neither the map server nor the client, ever reads it.
 */
export const WALKABLE = 128;

const WATER_COLOUR = 1;
const ROOM_PREFIX = "room - ";
const ROOM_FLOOR_COLOUR = 1;
const WATER_WORDS = ["water", "coast", "beach", "river", "deep sea"];
const WALKABLE_WORDS = ["dock", "frozen", "passage"];
const HAZARD_WORDS = ["lava", "spike", "blood"];
const EDGE_WORDS = ["cliff", "platform", "plat-"];
const EDGE_GROUND_WORDS = ["ground", "grass", "shading", "coast"];

/**
 * The share of a quarter cell the water colour must reach, by the corner mask, to block it: a straight
 * shore then blocks three of a tile's four rows and leaves the bank walkable.
 */
export const SHORE_THRESHOLD = 0.35;

/**
 * The values an author can paint into a collision layer: the three that block, and Walkable, which opens
 * the ground under it.
 */
export const COLLISION_BRUSHES: readonly CollisionBrush[] = [
  { name: "Solid", value: SOLID },
  { name: "Water", value: WATER },
  { name: "Hole", value: HOLE },
  { name: "Walkable", value: WALKABLE },
];

/**
 * Whether a name contains any of the words.
 *
 * @param name Lower-case wang set name.
 * @param words Words to look for.
 * @returns True when one matches.
 */
function has(name: string, words: readonly string[]): boolean {
  return words.some((word) => name.includes(word));
}

/**
 * A shore rule when the set paints colour 1, else a cells rule: the set blocks its own side of every
 * painted tile at quarter-cell resolution, leaving the bank walkable.
 *
 * @param terrain The terrain.
 * @param value The flag to write.
 * @returns The rule.
 */
function shoreOrCells(terrain: TerrainDefinition, value: number): CollisionRule {
  if (terrain.colors.some((c) => c.index === WATER_COLOUR)) {
    return { kind: "corners", terrain: terrain.id, color: WATER_COLOUR, mode: "shore", threshold: SHORE_THRESHOLD, value };
  }
  return { kind: "cells", terrain: terrain.id, value };
}

/**
 * The rule a terrain contributes, by its wang set name, in this order: room brushes (a set named `room - ...` paints its
 * floor, colour 1, and draws walls around it, so every drawn cell but the floor is solid), fence sets, placed as tiles
 * (ADR-033), make every tile of the set solid; walls are solid wherever a tile is drawn (the rule set
 * grows wall faces below the painted top); docks, frozen lakes and lava passages are walkable; named holes
 * block by resolved cell; hazards (lava, spikes, blood pools) block like holes, at the shore share when the
 * set paints colour 1, else by cell; cliff and platform face sets are solid like walls, but a set naming the ground on a platform (grass,
 * ground, shading, coast) is ground; water sets block the water side of every painted tile at quarter-cell
 * resolution (`shore`), so a bank stays walkable while the water beside it does not.
 *
 * @param terrain The terrain.
 * @returns The rule, or null when the terrain is walkable ground.
 */
function ruleFor(terrain: TerrainDefinition): CollisionRule | null {
  const name = terrain.name.toLowerCase();
  if (name.startsWith(ROOM_PREFIX)) {
    return { kind: "cells", terrain: terrain.id, value: SOLID, exceptInterior: ROOM_FLOOR_COLOUR };
  }
  if (name.includes("fence")) {
    return { kind: "tiles", tileset: terrain.tileset, tiles: [...new Set(terrain.tiles.map((t) => t.tile))].sort((a, b) => a - b), value: SOLID };
  }
  if (name.includes("wall")) {
    return { kind: "cells", terrain: terrain.id, value: SOLID };
  }
  if (has(name, WALKABLE_WORDS)) {
    return null;
  }
  if (name.includes("hole")) {
    return { kind: "cells", terrain: terrain.id, value: HOLE };
  }
  if (has(name, HAZARD_WORDS)) {
    return shoreOrCells(terrain, HOLE);
  }
  if (has(name, EDGE_WORDS) && !has(name, EDGE_GROUND_WORDS)) {
    return { kind: "cells", terrain: terrain.id, value: SOLID };
  }
  if (has(name, WATER_WORDS) && terrain.colors.some((c) => c.index === WATER_COLOUR)) {
    return { kind: "corners", terrain: terrain.id, color: WATER_COLOUR, mode: "shore", threshold: SHORE_THRESHOLD, value: WATER };
  }
  return null;
}

/**
 * OdysseyTales' collision rules for the terrains of a world. Beyond the map is solid, and `WALKABLE` paint
 * opens what the terrain rules block.
 *
 * @param terrains Terrains of the compile context.
 * @returns The rules.
 */
export function collisionRulesFor(terrains: readonly TerrainDefinition[]): CollisionRules {
  const rules: CollisionRule[] = [];
  for (const terrain of terrains) {
    const rule = ruleFor(terrain);
    if (rule !== null) {
      rules.push(rule);
    }
  }
  rules.push({ kind: "footprints", value: SOLID });
  return { rules, outside: SOLID, open: WALKABLE };
}
